import {
  PhoneCall,
  Target,
  Sparkles,
  Megaphone,
  TrendingUp,
  Code2,
  Layout,
  Headset,
  Users,
  DraftingCompass,
} from "lucide-react";

// Canonical site URL for SEO (canonical tags, Open Graph, JSON-LD, sitemap).
// Read from env so it is not hardcoded; falls back to the current origin, then the production domain.
export const SITE_URL =
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  "https://www.growthsyncbpo.com";

export const CONTACT_INFO = {
  phone: "+880 1611-746260",
  phoneRaw: "+8801611746260",
  whatsapp: "https://wa.me/8801611746260",
  email: "info@growthsyncbpo.com",
  address:
    "House# 789, Road# 11, Avenue# 4, Mirpur DOHS, Mirpur, Dhaka-1216, Bangladesh",
};

export const SERVICES = [
  {
    name: "Telemarketing",
    slug: "telemarketing",
    icon: PhoneCall,
    description:
      "Outbound calling that qualifies prospects and books meetings — scripted, trained, and tracked by professional agents.",
    tagline: "Professional outbound calling that turns cold lists into booked meetings.",
    seoTitle: "Telemarketing Services | Outbound Calling & Appointment Setting — GrowthSync Solutions",
    seoDescription:
      "Professional B2B & B2C telemarketing by GrowthSync Solutions. Trained English-speaking agents, appointment setting, and qualified outreach at cost-effective rates.",
    longDescription: [
      "Cold outreach only works when it's done by people who know how to hold a conversation. Our telemarketing teams are trained on your product, your market, and your objections — so every call sounds like it's coming from inside your company, not from a script mill.",
      "From appointment setting for B2B sales teams to direct B2C campaigns, we handle list management, calling, follow-ups, and CRM logging end-to-end. You get booked meetings and clean data — we handle everything else.",
    ],
    features: [
      "Appointment setting & meeting booking",
      "B2B & B2C outbound campaigns",
      "Trained English-speaking agents",
      "Custom call scripts & objection handling",
      "CRM logging & call recordings",
      "Daily & weekly performance reports",
    ],
    benefits: [
      "Predictable meeting pipeline every week",
      "Lower cost per meeting than in-house teams",
      "Brand-safe, quality-monitored conversations",
      "Scale agents up or down anytime",
    ],
  },
  {
    name: "Lead Generation",
    slug: "lead-generation",
    icon: Target,
    description:
      "Targeted lists and multi-channel outreach that fill your pipeline with real decision-makers, not dead ends.",
    tagline: "A pipeline full of decision-makers — built, verified, and delivered to you.",
    seoTitle: "Lead Generation Services | B2B Pipeline & Qualified Leads — GrowthSync Solutions",
    seoDescription:
      "GrowthSync Solutions builds qualified B2B lead pipelines with verified data, multi-channel outreach, and lead scoring. Fill your sales calendar with decision-makers.",
    longDescription: [
      "Most 'leads' are just names on a spreadsheet. We build pipelines of verified, qualified decision-makers who match your ideal customer profile — sourced through research, enriched with accurate contact data, and warmed up through multi-channel outreach.",
      "Calls, emails, and LinkedIn touches work together in coordinated sequences. Every lead is scored and qualified before it reaches your sales team, so your closers spend time closing — not chasing.",
    ],
    features: [
      "Targeted prospect list building",
      "Multi-channel outreach (calls, email, LinkedIn)",
      "Lead qualification & scoring",
      "Appointment-ready handoffs",
      "Verified, GDPR-conscious contact data",
      "Campaign analytics & reporting",
    ],
    benefits: [
      "Consistent flow of sales-ready leads",
      "Higher conversion from better targeting",
      "No wasted time on unqualified prospects",
      "Full visibility into every campaign",
    ],
  },
  {
    name: "AI-Driven Marketing",
    slug: "ai-driven-marketing",
    icon: Sparkles,
    description:
      "Automation and AI insights that sharpen targeting, creatives, and conversion rates across every campaign.",
    tagline: "Marketing that learns — AI targeting, automation, and optimization on every campaign.",
    seoTitle: "AI-Driven Marketing Services | Automation & Smart Campaigns — GrowthSync Solutions",
    seoDescription:
      "Leverage AI marketing with GrowthSync Solutions: predictive targeting, automated optimization, AI content, and chat automation that lift conversion rates and cut costs.",
    longDescription: [
      "AI isn't a buzzword to us — it's a toolkit we use daily to make campaigns cheaper and sharper. We deploy AI for audience segmentation, predictive lead scoring, automated ad optimization, and creative testing at a speed no manual team can match.",
      "We also build practical automations: chatbots that qualify visitors, workflows that nurture leads while you sleep, and dashboards that tell you exactly what's working. The result is marketing that compounds — every campaign learns from the last one.",
    ],
    features: [
      "AI audience targeting & segmentation",
      "Automated campaign optimization",
      "Predictive lead scoring",
      "AI content & creative assistance",
      "Chatbot & workflow automation",
      "Real-time performance dashboards",
    ],
    benefits: [
      "Lower acquisition costs through smarter targeting",
      "Campaigns optimize themselves 24/7",
      "Faster creative testing & iteration",
      "Data-backed decisions, not guesses",
    ],
  },
  {
    name: "Digital Marketing",
    slug: "digital-marketing",
    icon: Megaphone,
    description:
      "Full-funnel campaign strategy, content, and performance optimization across the channels that matter.",
    tagline: "Full-funnel campaigns across the channels your customers actually use.",
    seoTitle: "Digital Marketing Services | Social, PPC & Content — GrowthSync Solutions",
    seoDescription:
      "GrowthSync Solutions runs full-funnel digital marketing: social media, Google & Meta ads, content, and email campaigns — strategy to execution with transparent reporting.",
    longDescription: [
      "Scattered posts and random ads don't grow businesses — systems do. We build full-funnel digital marketing programs: strategy, content, paid campaigns, and email flows that move people from 'never heard of you' to 'take my money'.",
      "Our team manages Google, Meta, and LinkedIn ads alongside organic social and email marketing, constantly reallocating budget to what performs. You see everything in a clear monthly report — spend, results, and next steps.",
    ],
    features: [
      "Social media marketing & management",
      "Paid ads (Google, Meta, LinkedIn)",
      "Content strategy & creation",
      "Email marketing campaigns",
      "Funnel & conversion optimization",
      "Monthly analytics & growth reports",
    ],
    benefits: [
      "One team for every channel",
      "Budget flows to what performs",
      "Consistent brand voice everywhere",
      "Clear ROI reporting monthly",
    ],
  },
  {
    name: "SEO",
    slug: "seo",
    icon: TrendingUp,
    description:
      "Technical and content SEO that compounds — more organic traffic, better rankings, sustainable leads.",
    tagline: "Rank higher, get found, and turn search traffic into a lead machine.",
    seoTitle: "SEO Services | Technical & Content SEO That Ranks — GrowthSync Solutions",
    seoDescription:
      "GrowthSync Solutions delivers technical SEO, keyword strategy, content, and link building that compound into lasting rankings, organic traffic, and inbound leads.",
    longDescription: [
      "SEO is the only marketing channel that gets cheaper over time — if it's done right. We start with a deep technical audit, fix what's holding your site back, and build a keyword strategy around terms your buyers actually search.",
      "Then we execute relentlessly: optimized pages, helpful content, quality links, and local SEO for map visibility. Rankings compound month over month, turning your website into a lead source that doesn't need an ad budget.",
    ],
    features: [
      "Technical SEO audits & fixes",
      "Keyword research & strategy",
      "On-page optimization",
      "Content writing & link building",
      "Local SEO & Google Business Profile",
      "Rank tracking & monthly reporting",
    ],
    benefits: [
      "Traffic that compounds without ad spend",
      "Higher-intent visitors who convert",
      "Long-term defensible rankings",
      "Full transparency on every change",
    ],
  },
  {
    name: "Software Development",
    slug: "software-development",
    icon: Code2,
    description:
      "Custom web apps and internal tools engineered for speed, reliability, and scale by senior developers.",
    tagline: "Custom software built fast, built right, and built to scale.",
    seoTitle: "Software Development Services | Web Apps & Custom Tools — GrowthSync Solutions",
    seoDescription:
      "GrowthSync Solutions builds custom web applications, internal tools, and API integrations — senior developers, agile delivery, and ongoing support at offshore rates.",
    longDescription: [
      "Off-the-shelf software forces your business to work its way. We build software that works your way — custom web applications, internal dashboards, and integrations designed around your exact workflows.",
      "Our senior developers ship in agile sprints with weekly demos, so you see progress every week and can change direction without burning budget. After launch, we stay on for maintenance, monitoring, and new features.",
    ],
    features: [
      "Custom web application development",
      "Internal tools & dashboards",
      "API development & integrations",
      "Database design & optimization",
      "QA testing & ongoing maintenance",
      "Cloud deployment & scaling",
    ],
    benefits: [
      "Senior talent at offshore rates",
      "Weekly demos — never a black box",
      "Built to scale from day one",
      "Long-term support included",
    ],
  },
  {
    name: "Website Design",
    slug: "website-design",
    icon: Layout,
    description:
      "Modern, responsive websites designed to convert visitors into inquiries — beautiful and built to perform.",
    tagline: "Websites that look premium and convert visitors into customers.",
    seoTitle: "Website Design Services | Modern Converting Websites — GrowthSync Solutions",
    seoDescription:
      "GrowthSync Solutions designs modern, responsive, SEO-friendly websites and landing pages engineered to convert visitors into inquiries and sales.",
    longDescription: [
      "Your website is your hardest-working salesperson — or your biggest leak. We design modern, fast, mobile-first websites where every section has a job: build trust, answer objections, and drive visitors toward action.",
      "Every build is responsive, SEO-ready, and easy to update. Whether you need a corporate site, a high-converting landing page, or a full e-commerce store, we deliver something you'll be proud to send traffic to.",
    ],
    features: [
      "Custom responsive design",
      "High-converting landing pages",
      "E-commerce websites",
      "CMS & easy content editing",
      "Speed & SEO-friendly builds",
      "Ongoing support & updates",
    ],
    benefits: [
      "More inquiries from the same traffic",
      "Flawless on every device",
      "Fast load times that Google rewards",
      "Easy to update without a developer",
    ],
  },
  {
    name: "Customer Support",
    slug: "customer-support",
    icon: Headset,
    description:
      "24/7-ready, multi-channel support workflows that protect your brand experience and keep customers loyal.",
    tagline: "24/7 support that protects your brand and keeps customers coming back.",
    seoTitle: "Customer Support Outsourcing | 24/7 Multi-Channel Support — GrowthSync Solutions",
    seoDescription:
      "Outsource customer support to GrowthSync Solutions: 24/7 phone, email & live chat coverage with trained agents, ticketing management, and CSAT tracking.",
    longDescription: [
      "Every support ticket is a moment your brand is judged. Our trained support teams answer on your behalf across phone, email, and live chat — fast, friendly, and in your brand voice.",
      "We set up ticketing workflows, escalation paths, and QA reviews so quality never slips. With 24/7 coverage from Dhaka, your customers get answers at 3 AM while your in-house team sleeps.",
    ],
    features: [
      "24/7 multi-channel coverage",
      "Phone, email & live chat support",
      "Ticketing system management",
      "Trained brand-aligned agents",
      "Escalation workflows & QA reviews",
      "CSAT tracking & reporting",
    ],
    benefits: [
      "Faster response times, happier customers",
      "Around-the-clock coverage",
      "Fraction of in-house support cost",
      "Quality monitored on every interaction",
    ],
  },
  {
    name: "Virtual Assistants",
    slug: "virtual-assistants",
    icon: Users,
    description:
      "Reliable, trained VAs for admin, research, scheduling, and day-to-day operations — your remote right hand.",
    tagline: "Reliable remote assistants who take the busywork off your plate.",
    seoTitle: "Virtual Assistant Services | Trained Remote VAs — GrowthSync Solutions",
    seoDescription:
      "Hire trained virtual assistants from GrowthSync Solutions for admin, research, scheduling, inbox management, and operations — flexible plans, reliable delivery.",
    longDescription: [
      "Founders and executives lose hours every day to tasks someone else should be doing. Our virtual assistants are trained professionals who take over your admin, inbox, calendar, research, and data work — reliably and confidentially.",
      "Each VA is matched to your needs and backed by our quality team, so there's always coverage and consistency. Start with a few hours a week and scale as you grow.",
    ],
    features: [
      "Admin & calendar management",
      "Research & data entry",
      "Email & inbox management",
      "Travel & scheduling coordination",
      "Document & report preparation",
      "Flexible hourly plans",
    ],
    benefits: [
      "Reclaim 10+ hours every week",
      "Trained, vetted & confidential",
      "Backup coverage — never miss a day",
      "Scale hours up or down freely",
    ],
  },
  {
    name: "Architecture & Interior Design",
    slug: "architecture-interior-design",
    icon: DraftingCompass,
    description:
      "Concept-to-execution architecture and interior design for spaces that feel premium and function flawlessly.",
    tagline: "Spaces designed to impress — from first concept to final handover.",
    seoTitle: "Architecture & Interior Design Services — GrowthSync Solutions",
    seoDescription:
      "GrowthSync Solutions offers architecture and interior design: concept design, 3D visualization, space planning, and project supervision for homes and businesses.",
    longDescription: [
      "Great spaces don't happen by accident — they're engineered. Our architecture and interior design team takes projects from first sketch to final handover: concept design, 3D visualization, material selection, and on-site supervision.",
      "Whether it's a residence, office, restaurant, or retail space, we balance aesthetics with function and budget — delivering spaces that feel premium and work beautifully for the people inside them.",
    ],
    features: [
      "Architectural concept design",
      "3D visualization & photorealistic renders",
      "Interior design & space planning",
      "Material & furniture selection",
      "On-site project supervision",
      "Residential & commercial projects",
    ],
    benefits: [
      "See your space in 3D before building",
      "One team from concept to handover",
      "Premium results within budget",
      "Function and beauty, balanced",
    ],
  },
];

export const PROCESS_STEPS = [
  {
    step: "01",
    title: "Discover",
    description: "We learn your business, goals, and target market in a free consultation.",
  },
  {
    step: "02",
    title: "Strategize",
    description: "We design a tailored plan with clear deliverables, timelines, and KPIs.",
  },
  {
    step: "03",
    title: "Execute",
    description: "Our trained team launches within days — not months — and delivers consistently.",
  },
  {
    step: "04",
    title: "Report & Refine",
    description: "Weekly transparent reporting and continuous optimization for compounding results.",
  },
];

export const STATS = [
  { value: 500, suffix: "+", label: "Campaigns Delivered" },
  { value: 1.2, suffix: "M+", label: "Calls Handled", decimals: 1 },
  { value: 98, suffix: "%", label: "Client Retention" },
  { value: 24, suffix: "/7", label: "Global Delivery" },
];

export const WHY_CHOOSE_US = [
  {
    title: "Dedicated Teams",
    description:
      "Trained agents and specialists assigned exclusively to your account — an extension of your own team.",
  },
  {
    title: "AI-Powered Workflows",
    description:
      "We pair human expertise with AI automation so campaigns run smarter, faster, and cheaper.",
  },
  {
    title: "Transparent Reporting",
    description:
      "Weekly dashboards and clear KPIs. You always know exactly what we did and what it delivered.",
  },
  {
    title: "Cost-Effective Scale",
    description:
      "World-class delivery from Dhaka, Bangladesh — enterprise quality at a fraction of onshore cost.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "GrowthSync's lead generation team filled our pipeline with genuinely qualified prospects. The quality of the leads made a real difference to our close rate — these are decision-makers, not dead ends.",
    name: "Benjamin David Adams",
    role: "CEO, Kingdom Wealth Financial",
    location: "United States",
  },
  {
    quote:
      "The consistency is what impressed us most. Week after week, GrowthSync delivers verified, sales-ready leads that fit our ideal customer profile. Their process is transparent and the results speak for themselves.",
    name: "Evens Bellevue",
    role: "CEO, Impact Credit Solutions",
    location: "United States",
  },
  {
    quote:
      "Partnering with GrowthSync for lead generation was one of the best decisions we made this year. Their team understands our market, targets the right people, and hands us prospects that are ready to talk.",
    name: "Archie Patel",
    role: "CEO, RPJ Strategic Ventures",
    location: "United States",
  },
  {
    quote:
      "GrowthSync gave us a steady, reliable flow of high-quality leads without the overhead of building an in-house team. Professional, responsive, and genuinely invested in our growth.",
    name: "Wesley Jackson",
    role: "CEO, 1028 Capital",
    location: "United States",
  },
];

export const SERVICE_OPTIONS = [...SERVICES.map((s) => s.name), "Other / General Inquiry"];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
